import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Button } from "@/components/ui/button"
import { SlideExplosion } from "@/components/sections/SlideExplosion"
import gsap from "gsap"
import { trackFigmaPluginClick } from "@/lib/gtag"

export function Hero() {
    const sectionRef = useRef<HTMLElement>(null)
    const titleRef = useRef<HTMLHeadingElement>(null)
    const descRef = useRef<HTMLParagraphElement>(null)
    const ctaRef = useRef<HTMLDivElement>(null)

    const slidesRef = useRef<HTMLDivElement>(null)

    const { t } = useTranslation('landing')

    useEffect(() => {
        const ctx = gsap.context(() => {
            // モバイルではアニメーションを早く開始
            const isMobile = window.innerWidth < 768
            const startDelay = isMobile ? 0.15 : 0.4
            const tl = gsap.timeline({ defaults: { ease: 'power3.out' } })

            // Title lines stagger fade in from bottom
            if (titleRef.current) {
                const lines = titleRef.current.querySelectorAll('span > span, span.block')
                tl.fromTo(
                    lines,
                    { opacity: 0, y: isMobile ? 20 : 30 },
                    { opacity: 1, y: 0, duration: isMobile ? 0.7 : 1.0, stagger: isMobile ? 0.12 : 0.18 },
                    startDelay
                )
            }

            // Description fade in
            tl.fromTo(
                descRef.current,
                { opacity: 0, y: isMobile ? 15 : 20 },
                { opacity: 1, y: 0, duration: isMobile ? 0.6 : 0.8 },
                isMobile ? 0.5 : 0.9
            )

            // CTA fade in with scale
            tl.fromTo(
                ctaRef.current,
                { opacity: 0, y: isMobile ? 15 : 20, scale: 0.95 },
                { opacity: 1, y: 0, scale: 1, duration: isMobile ? 0.6 : 0.8 },
                isMobile ? 0.7 : 1.1
            )



            // Slides stagger in from center
            if (slidesRef.current) {
                const slides = slidesRef.current.querySelectorAll('.slide-item')
                if (slides.length > 0) {
                    tl.fromTo(
                        slides,
                        { opacity: 0, scale: 0.8, y: 50 },
                        {
                            opacity: 1,
                            scale: 1,
                            y: 0,
                            duration: 1.0,
                            stagger: { amount: 0.8, from: 'center' },
                            ease: 'back.out(1.2)'
                        },
                        0.6
                    )
                }
            }

            // Hands fade in
            if (slidesRef.current) {
                const hands = slidesRef.current.querySelectorAll('img[src*="hand"]')
                if (hands.length > 0) {
                    tl.fromTo(
                        hands,
                        { opacity: 0, y: -20 },
                        { opacity: 1, y: 0, duration: 0.6, stagger: 0.25, ease: 'back.out(4)' },
                        1.6
                    )
                }
            }
        }, sectionRef)

        return () => ctx.revert()
    }, [])

    return (
        <section ref={sectionRef} className="relative min-h-screen flex flex-col overflow-hidden">
            {/* Content Area */}
            <div className="container flex justify-center relative z-10 pt-[200px] md:pt-[140px] pb-[40px] md:py-[148px]">
                {/* Text Content */}
                <div className="flex flex-col items-start max-w-[280px] md:max-w-[256px]">
                    {/* Title */}
                    <h1 ref={titleRef} className="font-heading-en text-hero text-white leading-none mb-sm">
                        <span className="flex items-baseline gap-2">
                            <span className="opacity-0">{t('hero.title1')}</span>
                            <span className="italic opacity-0">{t('hero.title2')}</span>
                        </span>
                        <span className="block opacity-0">{t('hero.title3')}</span>
                    </h1>

                    {/* Description */}
                    <p ref={descRef} className="font-body-ja text-sm leading-relaxed mb-md max-w-md opacity-0">
                        {t('hero.description')}
                    </p>

                    {/* CTA */}
                    <div ref={ctaRef} className="flex flex-col items-center gap-xs opacity-0">
                        <Button variant="cta" size="lg" asChild className="w-full font-bold">
                            <a
                                href="https://www.figma.com/community/plugin/1557378742956260910"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackFigmaPluginClick('hero_cta')}
                            >
                                {t('common:cta.startDigging')}
                            </a>
                        </Button>
                        <p className="font-body-en text-[10px] text-white/60 text-center">
                            {t('common:cta.figmaNote')}
                        </p>
                    </div>
                </div>
            </div>



            {/* Slide Explosion Visual */}
            <div ref={slidesRef} className="w-full max-w-[1440px] mx-auto relative z-0">
                <SlideExplosion />
            </div>
        </section>
    )
}
